/**
 * Copy to Clipboard Module
 *
 * Handles copy-to-clipboard functionality for share buttons.
 * Copies the current page URL to clipboard and provides visual feedback.
 *
 * @since 1.0.0
 */

/**
 * Initialize copy-to-clipboard functionality
 *
 * Adds event listeners to share buttons that copy the current URL
 * to the clipboard and provide user feedback.
 *
 * @since 1.0.0
 * @returns {void}
 */
export function initCopyToClipboard() {
	const shareButtons = document.querySelectorAll( 'a[href="#share"]' );

	// Exit early if no share buttons are found.
	if ( 0 === shareButtons.length ) {
		return;
	}

	/**
	 * Show temporary confirmation message
	 *
	 * Displays a confirmation message and removes it after a short delay.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} button - The share button element.
	 * @returns {void}
	 */
	function showConfirmation( button ) {
		const originalText = button.textContent;
		const originalAriaLabel = button.getAttribute( 'aria-label' );

		// Update button text and aria-label
		button.textContent = 'Copied';
		button.setAttribute( 'aria-label', 'URL copied to clipboard' );
		button.classList.add( 'is-copied' );

		// Reset after 2 seconds
		setTimeout( () => {
			button.textContent = originalText;
			button.setAttribute( 'aria-label', originalAriaLabel );
			button.classList.remove( 'is-copied' );
		}, 2000 );
	}

	/**
	 * Handle share button click
	 *
	 * Copies the current URL to the clipboard and provides
	 * visual feedback to the user.
	 *
	 * @since 1.0.0
	 * @param {Event} event - The click event object.
	 * @returns {void}
	 */
	function handleShareClick( event ) {
		event.preventDefault();

		const button = event.currentTarget;
		const urlToCopy = window.location.href;

		// Use modern Clipboard API
		if ( navigator.clipboard && navigator.clipboard.writeText ) {
			navigator.clipboard
				.writeText( urlToCopy )
				.then( () => {
					showConfirmation( button );
				} )
				.catch( ( error ) => {
					console.error( 'Failed to copy URL:', error );
					// Fallback to older method if modern API fails
					fallbackCopy( urlToCopy, button );
				} );
		} else {
			// Fallback for older browsers
			fallbackCopy( urlToCopy, button );
		}
	}

	/**
	 * Fallback copy method for older browsers
	 *
	 * Uses the deprecated document.execCommand method as a fallback.
	 *
	 * @since 1.0.0
	 * @param {string} text - The text to copy.
	 * @param {HTMLElement} button - The share button element.
	 * @returns {void}
	 */
	function fallbackCopy( text, button ) {
		const textArea = document.createElement( 'textarea' );
		textArea.value = text;
		textArea.style.position = 'fixed';
		textArea.style.left = '-999999px';
		document.body.appendChild( textArea );

		try {
			textArea.select();
			document.execCommand( 'copy' );
			showConfirmation( button );
		} catch ( error ) {
			console.error( 'Failed to copy URL using fallback:', error );
		} finally {
			document.body.removeChild( textArea );
		}
	}

	// Attach event listeners to all share buttons
	shareButtons.forEach( ( button ) => {
		button.addEventListener( 'click', handleShareClick );
	} );
}
